import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TextField,
  Chip,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import api from '../../services/api';

const CustomCheckbox = withStyles({
  root: {
    color: '#8b0304',
    '&$checked': {
      color: '#8b0304',
    },
  },
  checked: {},
})(Checkbox);

function CommunicationDialog({
  open,
  onClose,
  professionals,
  projectProfessionalManager,
  projectProfessionalPartner,
  weeks,
  projectId,
  projectName,
  projectCode,
}) {
  const [comparisonData, setComparisonData] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateText, setTemplateText] = useState('');
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [professionalsDialogOpen, setProfessionalsDialogOpen] = useState(false);
  const [selectedProfessionalIds, setSelectedProfessionalIds] = useState([]);
  const [professionalsList, setProfessionalsList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBlocks, setSelectedBlocks] = useState({});

  const loadProfessionals = useCallback(async () => {
    try {
      const response = await api.get(`master/professionals`);
      setProfessionalsList(response.data);
    } catch (ex) {
      console.log('Erro ao carregar professionals', ex);
    }
  }, []);

  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);

  const addDomainIfMissing = email => {
    if (!email) return '';
    const trimmed = email.trim();
    return trimmed.includes('@')
      ? trimmed
      : `${trimmed}@integrationconsulting.com`;
  };

  // useEffect(() => {
  //   const initialEmails = [];

  //   if (projectProfessionalManager) {
  //     initialEmails.push(addDomainIfMissing(projectProfessionalManager));
  //   }
  //   if (projectProfessionalPartner) {
  //     initialEmails.push(addDomainIfMissing(projectProfessionalPartner));
  //   }

  //   // adiciona os e-mails fixos
  //   initialEmails.push('deliverycell@integrationconsulting.com');
  //   initialEmails.push('assistentes@integrationconsulting.com');
  //   initialEmails.push('gmascarenhas@integrationconsulting.com');
  //   initialEmails.push('legal@integrationconsulting.com');
  //   initialEmails.push('vruggiero@integrationconsulting.com');

  //   setEmails(initialEmails);
  // }, [projectProfessionalManager, projectProfessionalPartner]);

  useEffect(() => {
    if (open && projectId) {
      api.get(`scheduling/communication/${projectId}`).then(response => {
        setComparisonData(response.data);
      });
    }
  }, [open, projectId]);

  const formatDate = dateStr => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const filteredChanges = useMemo(() => {
    if (!comparisonData || !comparisonData.changes) return [];
    const today = new Date();
    const currentSunday = new Date(today);
    currentSunday.setDate(today.getDate() - today.getDay());
    currentSunday.setHours(0, 0, 0, 0);
    return comparisonData.changes.filter(change => {
      const weekId =
        change.IDProjectScheduleWeek !== null
          ? change.IDProjectScheduleWeek
          : change.IDScheduleWeek;
      const week = weeks.find(w => String(w.ID) === String(weekId));
      if (!week) return false;
      const weekStart = new Date(week.StartDate);
      return weekStart >= currentSunday && change.Current !== change.Previous;
    });
  }, [comparisonData, weeks]);

  const processedChanges = useMemo(() => {
    if (!filteredChanges.length || !comparisonData) return [];
    const groups = {};
    filteredChanges.forEach(change => {
      const profId = change.IDProfessional;
      if (!groups[profId]) groups[profId] = [];
      groups[profId].push(change);
    });
    const newChanges = [];
    Object.keys(groups).forEach(profId => {
      const allChangesForProf = comparisonData.changes.filter(
        c => c.IDProfessional === Number(profId),
      );
      const allNewAllocations = allChangesForProf
        .filter(c => c.Current > 0)
        .every(c => c.Previous === 0);
      const group = groups[profId].sort((a, b) => {
        const weekAId =
          a.IDProjectScheduleWeek !== null
            ? a.IDProjectScheduleWeek
            : a.IDScheduleWeek;
        const weekBId =
          b.IDProjectScheduleWeek !== null
            ? b.IDProjectScheduleWeek
            : b.IDScheduleWeek;
        const weekA = weeks.find(w => String(w.ID) === String(weekAId));
        const weekB = weeks.find(w => String(w.ID) === String(weekBId));
        return (
          new Date(weekA ? weekA.StartDate : 0) -
          new Date(weekB ? weekB.StartDate : 0)
        );
      });
      group.forEach((change, idx) => {
        const weekId =
          change.IDProjectScheduleWeek !== null
            ? change.IDProjectScheduleWeek
            : change.IDScheduleWeek;
        const currentWeek = weeks.find(w => String(w.ID) === String(weekId));
        const currentWeekStart = currentWeek
          ? new Date(currentWeek.StartDate)
          : new Date(0);
        const futureRecords = allChangesForProf.filter(c => {
          const cWeekId =
            c.IDProjectScheduleWeek !== null
              ? c.IDProjectScheduleWeek
              : c.IDScheduleWeek;
          const cWeek = weeks.find(w => String(w.ID) === String(cWeekId));
          if (!cWeek) return false;
          return new Date(cWeek.StartDate) > currentWeekStart;
        });
        const hasFutureAllocation = futureRecords.some(c => c.Current > 0);
        let type = '';
        if (change.Current > 0) {
          if (allNewAllocations) {
            type = 'New Allocation';
          } else {
            if (change.Previous === 0) {
              const sameWeekExists = group.slice(0, idx).some(c => {
                const cWeekId =
                  c.IDProjectScheduleWeek !== null
                    ? c.IDProjectScheduleWeek
                    : c.IDScheduleWeek;
                return String(cWeekId) === String(weekId);
              });
              type =
                idx === 0 || sameWeekExists ? 'New Allocation' : 'Extension';
            } else {
              type = 'Dedication Adjustment';
            }
          }
        } else if (change.Current === 0 && change.Previous > 0) {
          type = hasFutureAllocation ? 'Dedication Adjustment' : 'Deallocation';
        }
        newChanges.push({ ...change, changeType: type });
      });
    });
    return newChanges;
  }, [filteredChanges, weeks, comparisonData]);

  const mergedChanges = useMemo(() => {
    if (!processedChanges.length) return [];
    const merged = [];
    const groupsByProfessional = processedChanges.reduce((acc, change) => {
      const profId = change.IDProfessional;
      if (!acc[profId]) acc[profId] = [];
      acc[profId].push(change);
      return acc;
    }, {});
    Object.keys(groupsByProfessional).forEach(profId => {
      const changes = groupsByProfessional[profId].sort(
        (a, b) => new Date(a.startDate) - new Date(b.startDate),
      );
      let currentGroup = null;
      changes.forEach(change => {
        const weekId =
          change.IDProjectScheduleWeek !== null
            ? change.IDProjectScheduleWeek
            : change.IDScheduleWeek;
        const week = weeks.find(w => String(w.ID) === String(weekId));
        if (!week) return;
        if (!currentGroup) {
          currentGroup = {
            ...change,
            startDate: week.StartDate,
            endDate: week.EndDate,
          };
        } else {
          const currentWeekStart = new Date(week.StartDate);
          const lastGroupEnd = new Date(currentGroup.endDate);
          const diffInMs = currentWeekStart - lastGroupEnd;
          const oneDayInMs = 24 * 60 * 60 * 1000;
          if (
            change.Current === currentGroup.Current &&
            diffInMs === oneDayInMs
          ) {
            currentGroup.endDate = week.EndDate;
          } else {
            merged.push(currentGroup);
            currentGroup = {
              ...change,
              startDate: week.StartDate,
              endDate: week.EndDate,
            };
          }
        }
      });
      if (currentGroup) merged.push(currentGroup);
    });
    return merged;
  }, [processedChanges, weeks]);

  const computeBlocks = () => {
    if (!mergedChanges.length) return {};

    const formatWeekShort = dateStr => {
      const d = new Date(dateStr);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${dd}/${mm}`;
    };

    const groupsByProf = mergedChanges.reduce((acc, change) => {
      const profId = change.IDProfessional;
      if (!acc[profId]) acc[profId] = [];
      acc[profId].push(change);
      return acc;
    }, {});

    const blocks = {};

    Object.entries(groupsByProf).forEach(([profId, changes]) => {
      changes.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

      const prof = professionalsList.find(p => p.ID === Number(profId));
      if (!prof || prof.Name.toLowerCase().includes('tbd')) return;
      const profName = prof.Name;

      const types = Array.from(new Set(changes.map(c => c.changeType)));
      let primaryType = 'Deallocation';
      if (types.includes('New Allocation')) primaryType = 'New Allocation';
      else if (types.includes('Extension')) primaryType = 'Extension';
      else if (types.includes('Dedication Adjustment'))
        primaryType = 'Dedication Adjustment';

      const firstWeek = formatWeekShort(changes[0].startDate);
      let totalWeeks = 0;

      const segmentGroups = {};
      changes.forEach(c => {
        if (c.changeType === 'Deallocation') return;

        const start = new Date(c.startDate);
        const end = new Date(c.endDate);
        const weekDates = [];
        for (
          let dt = new Date(start);
          dt <= end;
          dt.setDate(dt.getDate() + 7)
        ) {
          weekDates.push(new Date(dt));
        }
        totalWeeks += weekDates.length;

        const key =
          c.Current >= 5 ? 'Full-time' : `${parseFloat(c.Current)} days/week`;

        if (!segmentGroups[key]) segmentGroups[key] = new Set();
        weekDates.forEach(d => segmentGroups[key].add(d.getTime()));
      });

      const sortedKeys = Object.keys(segmentGroups).sort((a, b) => {
        if (a === 'Full-time') return -1;
        if (b === 'Full-time') return 1;
        return parseFloat(b) - parseFloat(a);
      });

      // Monta os <li> com \n ao final
      let bulletListItems = '';
      sortedKeys.forEach(key => {
        const dates = Array.from(segmentGroups[key])
          .map(ts => new Date(ts))
          .sort((a, b) => a - b);

        const runs = [];
        let run = [dates[0]];
        for (let i = 1; i < dates.length; i++) {
          const prev = dates[i - 1];
          const curr = dates[i];
          if (curr - prev === 7 * 24 * 60 * 60 * 1000) {
            run.push(curr);
          } else {
            runs.push(run);
            run = [curr];
          }
        }
        runs.push(run);

        const parts = [];
        runs.forEach((r, runIndex) => {
          if (r.length > 3) {
            const start = formatWeekShort(r[0].toISOString());
            const end = formatWeekShort(r[r.length - 1].toISOString());
            if (runIndex === 0) {
              parts.push(`from ${start} until ${end} `);
            } else {
              parts.push(`than from ${start} until ${end} `);
            }
          } else {
            r.forEach(d => parts.push(formatWeekShort(d.toISOString())));
          }
        });

        const prefix = parts[0].startsWith('from')
          ? `on weeks ${parts.join(', ')}`
          : `on weeks of ${parts.join(', ')}`;

        // aqui inserimos a quebra de linha
        bulletListItems += `<li>${key}: ${prefix}</li>\n`;
      });

      let timeframeText = '';
      switch (primaryType) {
        case 'New Allocation':
          timeframeText = `${totalWeeks} weeks, on week of ${firstWeek}`;
          break;
        case 'Extension':
          timeframeText = `extended ${totalWeeks} weeks, on week of ${firstWeek}`;
          break;
        case 'Dedication Adjustment':
          timeframeText = `adjusted allocation for ${totalWeeks} weeks, on week of ${firstWeek}`;
          break;
        default:
          timeframeText = `you will be deallocated from the project, starting on week of ${firstWeek}`;
      }
      const timeframeLine = `<p><strong>Timeframe:</strong> ${timeframeText}</p>`;

      const dedicationLine =
        primaryType === 'Deallocation'
          ? `<p><strong>Dedication:</strong> --</p>`
          : `
            <p><strong>Dedication:</strong></p>
            <ul style="margin:0;padding-left:20px;">
              ${bulletListItems}
            </ul>`;

      const headerLine =
        primaryType === 'New Allocation'
          ? `<p><strong>New Allocation</strong></p>`
          : primaryType === 'Dedication Adjustment'
          ? `<p><strong>Dedication adjustment</strong></p>`
          : '';

      blocks[profId] = `
        <div style="margin-bottom:20px;padding:10px;border:1px solid #ddd;">
          ${headerLine}
          <p><strong>Professional:</strong> ${profName}</p>
          <p><strong>Project:</strong> ${projectName} - ${projectCode}</p>
          ${timeframeLine}
          ${dedicationLine}
        </div>
      `;
    });

    return blocks;
  };

  useEffect(() => {
    const blocks = computeBlocks();

    const initialSelected = Object.keys(blocks).reduce((acc, profId) => {
      acc[profId] = false;
      return acc;
    }, {});
    setSelectedBlocks(initialSelected);

    // 2) monta apenas os e-mails fixos + manager/partner
    const initial = [];
    if (projectProfessionalManager) {
      initial.push(addDomainIfMissing(projectProfessionalManager));
    }
    if (projectProfessionalPartner) {
      initial.push(addDomainIfMissing(projectProfessionalPartner));
    }

    const fixed = [
      'deliverycell@integrationconsulting.com',
      'assistentes@integrationconsulting.com',
      'gmascarenhas@integrationconsulting.com',
      'legal@integrationconsulting.com',
      'vruggiero@integrationconsulting.com',
    ];
    initial.push(...fixed);

    setEmails(Array.from(new Set(initial)));
  }, [
    projectProfessionalManager,
    projectProfessionalPartner,
    professionalsList,
    mergedChanges,
  ]);

  useEffect(() => {
    const blocks = computeBlocks();
    setSelectedBlocks(prev => {
      const newSelected = { ...prev };
      Object.keys(blocks).forEach(profId => {
        if (newSelected[profId] === undefined) newSelected[profId] = true;
      });
      return newSelected;
    });
  }, [mergedChanges]);

  const generateFinalTemplate = () => {
    const blocks = computeBlocks();
    const selectedBlockHtml = Object.keys(blocks)
      .filter(profId => (selectedBlocks[profId] ? true : false))
      .map(profId => blocks[profId])
      .join('');
    return selectedBlockHtml;
  };

  const generatePlainTemplate = () => {
    const htmlTemplate = generateFinalTemplate();
    return htmlTemplate.replace(/<\/?[^>]+(>|$)/g, '').trim();
  };

  const handleSendCommunication = async () => {
    try {
      const finalTemplate = generateFinalTemplate();
      const payload = {
        emails,
        subject: `Allocation Communication for Project ${projectName} - ${projectCode}`,
        message: finalTemplate,
      };
      const response = await api.post('scheduling/communication/send', payload);
      console.log('Email sent successfully:', response.data);
      onClose();
    } catch (error) {
      console.error('Error sending communication email:', error);
    }
  };

  const handleViewTemplate = () => {
    const template = generateFinalTemplate();
    setTemplateText(template);
    setTemplateDialogOpen(true);
  };

  const handleViewPlainTemplate = () => {
    const plainTemplate = generatePlainTemplate();
    setTemplateText(plainTemplate);
    setTemplateDialogOpen(true);
  };

  const handleAddEmail = () => {
    const emailToAdd = addDomainIfMissing(newEmail);
    if (emailToAdd && !emails.includes(emailToAdd)) {
      setEmails(prevEmails => [...prevEmails, emailToAdd]);
      setNewEmail('');
    }
  };

  const handleDeleteEmail = emailToDelete => {
    setEmails(prevEmails =>
      prevEmails.filter(email => email !== emailToDelete),
    );
  };

  const handleOpenProfessionalsDialog = () => {
    setProfessionalsDialogOpen(true);
  };

  const handleCloseProfessionalsDialog = () => {
    setProfessionalsDialogOpen(false);
    setSelectedProfessionalIds([]);
    setSearchQuery('');
  };

  const handleToggleProfessional = id => {
    setSelectedProfessionalIds(prev =>
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id],
    );
  };

  const handleSaveSelectedProfessionals = () => {
    selectedProfessionalIds.forEach(id => {
      const prof = professionalsList.find(p => p.ID === id);
      if (prof && prof.Email) {
        const emailToAdd = addDomainIfMissing(prof.Email);
        setEmails(prev =>
          prev.includes(emailToAdd) ? prev : [...prev, emailToAdd],
        );
      }
    });
    handleCloseProfessionalsDialog();
  };

  const filteredProfessionalsList = useMemo(() => {
    return professionalsList.filter(prof => {
      const name = prof.Name || '';
      return name.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [professionalsList, searchQuery]);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          <Box display="flex" flexDirection="column" width="100%">
            <Typography variant="h6">
              Allocation Comparison for Project {projectName} - {projectCode}
            </Typography>
            <Box mt={1} width="100%">
              <Button
                variant="outlined"
                onClick={handleViewPlainTemplate}
                style={{ fontSize: '0.8rem' }}
              >
                View Plain Template
              </Button>
              <Box mt={1} width="100%">
                <Typography variant="subtitle1" gutterBottom>
                  Emails that received the communication:
                </Typography>
                <Paper
                  elevation={3}
                  style={{
                    padding: '1rem',
                    marginBottom: '1rem',
                    width: '100%',
                  }}
                >
                  <Box display="flex" flexWrap="wrap" style={{ gap: '0.5rem' }}>
                    {emails.map((email, index) => (
                      <Chip
                        key={index}
                        label={email}
                        onDelete={() => handleDeleteEmail(email)}
                      />
                    ))}
                  </Box>
                </Paper>
                <Box
                  display="flex"
                  alignItems="center"
                  width="100%"
                  style={{ gap: '0.5rem' }}
                >
                  <TextField
                    label="Add Email"
                    variant="outlined"
                    size="small"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          @integrationconsulting.com
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddEmail}
                  >
                    Add
                  </Button>
                </Box>
                <Box mt={1}>
                  <Button
                    variant="outlined"
                    onClick={handleOpenProfessionalsDialog}
                  >
                    Choose Professional E-mail
                  </Button>
                </Box>
                <Box mt={2}>
                  <Typography variant="subtitle1">
                    Select professionals to include in the communication email
                    (see template below):
                  </Typography>
                  <Box
                    style={{
                      backgroundColor: '#fff',
                      padding: '10px',
                      borderRadius: '4px',
                    }}
                  >
                    <Box
                      bgcolor="#fff"
                      p={2}
                      borderRadius={4}
                      display="flex"
                      flexWrap="wrap"
                      gap={1}
                    >
                      {Object.entries(computeBlocks()).map(([profId]) => {
                        const prof = professionalsList.find(
                          p => p.ID === Number(profId),
                        );
                        const email =
                          prof?.Email && addDomainIfMissing(prof.Email);
                        return (
                          <Box key={profId} display="flex" alignItems="center">
                            <CustomCheckbox
                              checked={Boolean(selectedBlocks[profId])}
                              onChange={(_, checked) => {
                                setSelectedBlocks(prev => ({
                                  ...prev,
                                  [profId]: checked,
                                }));
                                if (email) {
                                  setEmails(prev => {
                                    const set = new Set(prev);
                                    if (checked) set.add(email);
                                    else set.delete(email);
                                    return Array.from(set);
                                  });
                                }
                              }}
                            />
                            <Typography
                              style={{ cursor: 'default', userSelect: 'none' }}
                            >
                              {prof?.Name || 'N/A'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Paper elevation={3} style={{ padding: '1rem', marginTop: '1rem' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Week</TableCell>
                  <TableCell>Professional</TableCell>
                  <TableCell>Previous</TableCell>
                  <TableCell>Current</TableCell>
                  <TableCell>Change Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mergedChanges.map(change => {
                  const prof = professionalsList.find(
                    p => p.ID === change.IDProfessional,
                  );
                  return (
                    <TableRow
                      key={`${change.IDProfessional}-${change.startDate}`}
                    >
                      <TableCell>{`From ${formatDate(
                        change.startDate,
                      )} until ${formatDate(change.endDate)}`}</TableCell>
                      <TableCell>{prof ? prof.Name : 'N/A'}</TableCell>
                      <TableCell>{change.Previous}</TableCell>
                      <TableCell>{change.Current}</TableCell>
                      <TableCell>{change.changeType}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="default">
            Cancel
          </Button>
          <Button onClick={handleSendCommunication} color="primary">
            Send Communication
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ style: { height: '100%' } }}
      >
        <DialogTitle>Email Template</DialogTitle>
        <DialogContent style={{ height: '70vh' }}>
          <TextField
            value={templateText}
            onChange={e => setTemplateText(e.target.value)}
            multiline
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiInputBase-inputMultiline': {
                height: '100%',
                overflow: 'auto',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={professionalsDialogOpen}
        onClose={handleCloseProfessionalsDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Professionals</DialogTitle>
        <DialogContent>
          <TextField
            label="Search Professional"
            variant="outlined"
            size="small"
            fullWidth
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ marginBottom: '1rem' }}
          />
          <FormGroup>
            {filteredProfessionalsList.map(prof => (
              <FormControlLabel
                key={prof.ID}
                control={
                  <CustomCheckbox
                    checked={selectedProfessionalIds.includes(prof.ID)}
                    onChange={() => handleToggleProfessional(prof.ID)}
                  />
                }
                label={`${prof.Name} (${addDomainIfMissing(prof.Email)})`}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfessionalsDialog} color="default">
            Cancel
          </Button>
          <Button onClick={handleSaveSelectedProfessionals} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CommunicationDialog;
